import { Controller } from "@hotwired/stimulus";
import { animate } from "animejs";

function csrfToken() {
  return (document.querySelector('meta[name="csrf-token"]') || {}).content || "";
}

export default class extends Controller {
  static targets = ["login", "duration", "reason", "feedback", "chip"];
  static values = { msgDone: String, msgFailed: String };

  _fadeFeedback(holdMs) {
    clearTimeout(this._feedbackTimer);
    this.feedbackTarget.style.opacity = "1";
    this._feedbackTimer = setTimeout(() => {
      animate(this.feedbackTarget, {
        opacity: 0,
        duration: 400,
        ease: "outQuad",
        onComplete: () => {
          this.feedbackTarget.textContent = "";
          this.feedbackTarget.style.opacity = "";
        },
      });
    }, holdMs);
  }

  setReason(event) {
    if (this.hasReasonTarget) {
      this.reasonTarget.value = event.params.reason;
      this.reasonTarget.focus();
    }
    this.chipTargets.forEach((c) => {
      c.classList.toggle("badge-primary", c === event.currentTarget);
      c.classList.toggle("badge-ghost", c !== event.currentTarget);
    });
  }

  clearChips() {
    this.chipTargets.forEach((c) => {
      c.classList.add("badge-ghost");
      c.classList.remove("badge-primary");
    });
  }

  _buildBody() {
    const body = { login: this.loginTarget.value.trim() };

    if (this.hasDurationTarget && this.durationTarget.value !== "") {
      body.duration = parseInt(this.durationTarget.value, 10);
    }

    if (this.hasReasonTarget && this.reasonTarget.value !== "") {
      body.reason = this.reasonTarget.value;
    }

    return body;
  }

  submit(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const { url, method, confirm } = event.params;
    const body = this._buildBody();

    // Member-path actions (unban, unwarn) identify their target in the URL, so
    // the url param is a template. Submitting with the placeholder unfilled
    // would hit the collection path, which for DELETE means something else
    // entirely -- refuse instead (StreamCommand-nx5p).
    if (url.includes(":login") && !body.login) {
      this.feedbackTarget.textContent = this.msgFailedValue || "Failed";
      this._fadeFeedback(3000);
      return;
    }

    const target = url.replace(":login", encodeURIComponent(body.login));
    const message = confirm ? confirm.replace("%{user}", body.login || "") : null;

    if (message && window.Turbo?.config?.confirmMethod) {
      window.Turbo.config.confirmMethod(message, button, null).then((ok) => {
        if (ok) this._perform(button, target, method, body);
      });
    } else {
      this._perform(button, target, method, body);
    }
  }

  _perform(button, url, method, body) {
    button.disabled = true;

    const options = {
      method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        Accept: "application/json",
      },
    };
    if (method !== "GET" && method !== "HEAD") {
      options.body = JSON.stringify(body);
    }

    fetch(url, options)
      .then((response) => {
        if (response.ok) {
          this.loginTarget.value = "";
          if (this.hasDurationTarget) this.durationTarget.value = "";
          if (this.hasReasonTarget) this.reasonTarget.value = "";
          this.clearChips();
          this.feedbackTarget.textContent = this.msgDoneValue || "Done!";
          this._fadeFeedback(2000);
        } else {
          throw new Error("Request failed");
        }
      })
      .catch(() => {
        this.feedbackTarget.textContent = this.msgFailedValue || "Failed";
        this._fadeFeedback(3000);
      })
      .finally(() => {
        button.disabled = false;
      });
  }
}
